import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BACKEND_URL } from '../../utils/api';
import { useTraduzioni } from '../../i18n';
import './galleria.css';

// Galleria delle foto di un immobile (scheda del cliente e scheda dell'admin): foto principale in un riquadro di
// proporzioni fisse (la pagina non salta mentre si carica), miniature sotto che scorrono in orizzontale, e un
// visualizzatore a schermo intero con frecce, scorrimento a dita, contatore "3 / 20", chiusura con X ed Esc.

const LARGHEZZA = 1600;  // le foto dell'estrattore sono al massimo 1600 px (4:3)
const ALTEZZA = 1200;
const SOGLIA_DITO = 40;  // px di scorrimento per cambiare foto

export const urlFoto = (u) => (u && u.startsWith('/api') ? `${BACKEND_URL}${u}` : u);

const Freccia = ({ verso }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {verso === 'sinistra' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
  </svg>
);

// Scorrimento a dita: sinistra → successiva, destra → precedente
const useDito = (avanti, indietro) => {
  const inizio = useRef(null);
  return {
    onTouchStart: (e) => { const t = e.changedTouches[0]; inizio.current = { x: t.clientX, y: t.clientY }; },
    onTouchEnd: (e) => {
      if (!inizio.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - inizio.current.x;
      const dy = t.clientY - inizio.current.y;
      inizio.current = null;
      if (Math.abs(dx) < SOGLIA_DITO || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) avanti(); else indietro();
    },
  };
};

// Tab e Maiusc+Tab restano dentro il visore (è una finestra modale): dall'ultimo bottone si torna al primo
const BOTTONI = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
export const trattieniFocus = (e, finestra) => {
  if (e.key !== 'Tab' || !finestra) return;
  const bottoni = [...finestra.querySelectorAll(BOTTONI)];
  if (!bottoni.length) { e.preventDefault(); return; }
  const primo = bottoni[0];
  const ultimo = bottoni[bottoni.length - 1];
  const attivo = document.activeElement;
  if (!finestra.contains(attivo)) { e.preventDefault(); primo.focus(); }
  else if (e.shiftKey && attivo === primo) { e.preventDefault(); ultimo.focus(); }
  else if (!e.shiftKey && attivo === ultimo) { e.preventDefault(); primo.focus(); }
};

export const Visore = ({ foto, indice, onCambia, onChiudi, titolo }) => {
  const { t } = useTraduzioni();
  const nome = titolo || t('comune.foto');
  const n = foto.length;
  const chiudi = useRef(null);
  const finestra = useRef(null);
  const avanti = useCallback(() => onCambia((indice + 1) % n), [indice, n, onCambia]);
  const indietro = useCallback(() => onCambia((indice - 1 + n) % n), [indice, n, onCambia]);
  const dito = useDito(avanti, indietro);

  useEffect(() => {
    const prima = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    chiudi.current?.focus();
    return () => { document.body.style.overflow = overflow; prima?.focus?.(); };
  }, []);

  useEffect(() => {
    const tasto = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onChiudi(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); avanti(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); indietro(); }
      else trattieniFocus(e, finestra.current);
    };
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, [avanti, indietro, onChiudi]);

  // Le due vicine si caricano in anticipo: la freccia non aspetta la rete
  useEffect(() => {
    [indice + 1, indice - 1].forEach((i) => { const img = new Image(); img.src = urlFoto(foto[(i + n) % n]); });
  }, [indice, foto, n]);

  return createPortal(
    <div className="gf-visore" ref={finestra} role="dialog" aria-modal="true"
      aria-label={t('comune.foto_di_totale', { titolo: nome, n: indice + 1, totale: n })} data-testid="visore-foto">
      <div className="gf-barra">
        <span className="gf-contatore" aria-live="polite">{indice + 1} / {n}</span>
        <button type="button" ref={chiudi} className="gf-chiudi" onClick={onChiudi} aria-label={t('comune.chiudi')}>✕</button>
      </div>
      <div className="gf-palco" {...dito} onClick={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
        <img key={foto[indice]} src={urlFoto(foto[indice])} alt={t('comune.foto_alt', { titolo: nome, n: indice + 1, totale: n })} width={LARGHEZZA} height={ALTEZZA} decoding="async" />
      </div>
      {n > 1 && (
        <>
          <button type="button" className="gf-freccia sinistra" onClick={indietro} aria-label={t('comune.foto_precedente')}><Freccia verso="sinistra" /></button>
          <button type="button" className="gf-freccia destra" onClick={avanti} aria-label={t('comune.foto_successiva')}><Freccia verso="destra" /></button>
        </>
      )}
    </div>,
    document.body,
  );
};

const GalleriaFoto = ({ foto: tutte = [], titolo, className = '' }) => {
  const { t } = useTraduzioni();
  const nome = titolo || t('comune.foto');
  const [rotte, setRotte] = useState([]);
  const foto = tutte.filter((u) => !rotte.includes(u));
  const [indice, setIndice] = useState(0);
  const [aperto, setAperto] = useState(false);
  const striscia = useRef(null);
  const n = foto.length;
  const attuale = Math.min(indice, Math.max(0, n - 1));
  const rotta = (u) => () => setRotte((r) => (r.includes(u) ? r : [...r, u]));

  // La miniatura scelta resta visibile nella striscia
  useEffect(() => {
    const el = striscia.current?.children[attuale];
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [attuale]);

  if (!n) return <div className={`gf gf-vuota ${className}`} data-testid="galleria"><div className="gf-principale" /></div>;

  return (
    <div className={`gf ${className}`} data-testid="galleria">
      <button type="button" className="gf-principale" onClick={() => setAperto(true)}
        aria-label={t('comune.apri_foto', { n: attuale + 1, totale: n })}>
        <img src={urlFoto(foto[attuale])} alt={t('comune.foto_di', { titolo: nome, n: attuale + 1 })} width={LARGHEZZA} height={ALTEZZA}
          loading="eager" decoding="async" onError={rotta(foto[attuale])} />
        {n > 1 && <span className="gf-conta">{t('comune.conta_foto', { n })}</span>}
      </button>
      {n > 1 && (
        <div className="gf-miniature" ref={striscia} aria-label={t('comune.miniature')}>
          {foto.map((u, i) => (
            <button type="button" key={u} className={i === attuale ? 'on' : undefined} aria-current={i === attuale ? 'true' : undefined}
              aria-label={t('comune.foto_numero', { n: i + 1 })} onClick={() => setIndice(i)}>
              <img src={urlFoto(u)} alt="" width={160} height={120} loading="lazy" decoding="async" onError={rotta(u)} />
            </button>
          ))}
        </div>
      )}
      {aperto && <Visore foto={foto} indice={attuale} onCambia={setIndice} onChiudi={() => setAperto(false)} titolo={nome} />}
    </div>
  );
};

export default GalleriaFoto;
